/**
 * Panel Context
 * 
 * Manages active panels, state persistence, and panel lifecycle.
 * Part of Workstream 1: Navigation Restructure
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type PanelType = 'slideOver' | 'modal' | 'inline';

export interface PanelState {
  id: string;
  type: PanelType;
  title: string;
  content: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  data?: Record<string, unknown>;
  onClose?: () => void;
  persistent?: boolean;  // Keep state when minimized
}

interface PanelContextValue {
  // Current panels
  activePanels: PanelState[];
  minimizedPanels: PanelState[];
  
  // Panel actions
  openPanel: (panel: PanelState) => void;
  closePanel: (id: string) => void;
  minimizePanel: (id: string) => void;
  maximizePanel: (id: string) => void;
  closeAllPanels: () => void;
  
  // Quick access
  isOpen: (id: string) => boolean;
  getPanel: (id: string) => PanelState | undefined;
  updatePanelData: (id: string, data: Record<string, unknown>) => void;
}

const PanelContext = createContext<PanelContextValue | undefined>(undefined);

interface PanelProviderProps {
  children: ReactNode;
}

export function PanelProvider({ children }: PanelProviderProps) {
  const [activePanels, setActivePanels] = useState<PanelState[]>([]);
  const [minimizedPanels, setMinimizedPanels] = useState<PanelState[]>([]);
  
  const openPanel = useCallback((panel: PanelState) => {
    // Check if already open
    const existingIndex = activePanels.findIndex(p => p.id === panel.id);
    
    if (existingIndex >= 0) {
      // Bring to front
      setActivePanels(prev => [
        ...prev.slice(0, existingIndex),
        ...prev.slice(existingIndex + 1),
        panel,
      ]);
    } else {
      // Check if minimized
      const minimizedIndex = minimizedPanels.findIndex(p => p.id === panel.id);
      if (minimizedIndex >= 0) {
        const restored = minimizedPanels[minimizedIndex];
        setMinimizedPanels(prev => prev.filter(p => p.id !== panel.id));
        setActivePanels(prev => [...prev, { ...restored, ...panel }]);
      } else {
        setActivePanels(prev => [...prev, panel]);
      }
    }
  }, [activePanels, minimizedPanels]);
  
  const closePanel = useCallback((id: string) => {
    const panel = activePanels.find(p => p.id === id);
    if (panel?.onClose) {
      panel.onClose();
    }
    setActivePanels(prev => prev.filter(p => p.id !== id));
    setMinimizedPanels(prev => prev.filter(p => p.id !== id));
  }, [activePanels]);
  
  const minimizePanel = useCallback((id: string) => {
    const panel = activePanels.find(p => p.id === id);
    if (panel && panel.persistent) {
      setMinimizedPanels(prev => [...prev, panel]);
    }
    setActivePanels(prev => prev.filter(p => p.id !== id));
  }, [activePanels]);
  
  const maximizePanel = useCallback((id: string) => {
    const panel = minimizedPanels.find(p => p.id === id);
    if (panel) {
      setMinimizedPanels(prev => prev.filter(p => p.id !== id));
      setActivePanels(prev => [...prev, panel]);
    }
  }, [minimizedPanels]);
  
  const closeAllPanels = useCallback(() => {
    for (const panel of activePanels) {
      if (panel.onClose) {
        panel.onClose();
      }
    }
    setActivePanels([]);
    setMinimizedPanels([]);
  }, [activePanels]);
  
  const isOpen = useCallback((id: string) => {
    return activePanels.some(p => p.id === id);
  }, [activePanels]);
  
  const getPanel = useCallback((id: string) => {
    return activePanels.find(p => p.id === id) || minimizedPanels.find(p => p.id === id);
  }, [activePanels, minimizedPanels]);
  
  const updatePanelData = useCallback((id: string, data: Record<string, unknown>) => {
    setActivePanels(prev => prev.map(p => 
      p.id === id ? { ...p, data: { ...p.data, ...data } } : p
    ));
    setMinimizedPanels(prev => prev.map(p => 
      p.id === id ? { ...p, data: { ...p.data, ...data } } : p
    ));
  }, []);
  
  const value: PanelContextValue = {
    activePanels,
    minimizedPanels,
    openPanel,
    closePanel,
    minimizePanel,
    maximizePanel,
    closeAllPanels,
    isOpen,
    getPanel,
    updatePanelData,
  };
  
  return (
    <PanelContext.Provider value={value}>
      {children}
    </PanelContext.Provider>
  );
}

export function usePanels() {
  const context = useContext(PanelContext);
  if (!context) {
    throw new Error('usePanels must be used within a PanelProvider');
  }
  return context;
}

// Convenience hooks for specific panel types
export function useSlideOverPanel() {
  const { openPanel, closePanel } = usePanels();
  
  const open = useCallback((
    id: string,
    title: string,
    content: ReactNode,
    options?: Partial<Omit<PanelState, 'id' | 'title' | 'content' | 'type'>>
  ) => {
    openPanel({
      id,
      type: 'slideOver',
      title,
      content,
      size: 'lg',
      ...options,
    });
  }, [openPanel]);
  
  return { open, close: closePanel };
}

export function useModalPanel() {
  const { openPanel, closePanel, minimizePanel, maximizePanel } = usePanels();
  
  const open = useCallback((
    id: string,
    title: string,
    content: ReactNode,
    options?: Partial<Omit<PanelState, 'id' | 'title' | 'content' | 'type'>>
  ) => {
    openPanel({
      id,
      type: 'modal',
      title,
      content,
      size: 'md',
      persistent: true,
      ...options,
    });
  }, [openPanel]);
  
  return { open, close: closePanel, minimize: minimizePanel, maximize: maximizePanel };
}

export function useInlinePanel() {
  const { openPanel, closePanel } = usePanels();
  
  const open = useCallback((
    id: string,
    title: string,
    content: ReactNode,
    options?: Partial<Omit<PanelState, 'id' | 'title' | 'content' | 'type'>>
  ) => {
    openPanel({
      id,
      type: 'inline',
      title,
      content,
      ...options,
    });
  }, [openPanel]);
  
  return { open, close: closePanel };
}
